import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminSeminarDetail,
  fetchYowanAdminSeminarParticipantMailPreview,
  sendYowanAdminSeminarParticipantMail,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const previewStyle = {
  display: 'grid',
  gap: '0.45rem',
  padding: '0.85rem',
  borderRadius: '1rem',
  background: 'rgba(89, 102, 116, 0.08)',
};

const previewListStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.45rem',
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

const previewItemStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '1.85rem',
  padding: '0.25rem 0.72rem',
  borderRadius: '999px',
  background: 'rgba(116, 126, 115, 0.16)',
  color: '#2f363d',
  fontSize: '0.82rem',
  fontWeight: 600,
};

const previewGroupStyle = {
  display: 'grid',
  gap: '0.35rem',
};

const previewGroupTitleStyle = {
  margin: 0,
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#4a545d',
};

const fieldNoteStyle = {
  margin: 0,
  fontSize: '0.75rem',
  lineHeight: 1.45,
};

const getParticipantMailErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data || {};
  const detail = data.detail || data.error || data.message || '';
  const missingReason = data.missing_reason || data.missingReason || '';
  const code = data.code || '';
  const parts = [detail, missingReason].filter(Boolean);

  if (parts.length) {
    return `${fallbackMessage} ${parts.join(' / ')}`;
  }

  if (code) {
    return `${fallbackMessage} code=${code}`;
  }

  return fallbackMessage;
};

const AdminEventApplicationMailPage = () => {
  const content = yowanPageContent['/admin/events/:eventId/applications/mail'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');
  const applicationId = searchParams.get('applicationId');
  const slotId = searchParams.get('slotId');
  const isSingleContact = mode === 'single' && Boolean(applicationId);
  const isSlotContact = mode === 'slot' && Boolean(slotId);
  const audienceScope = isSingleContact ? 'application' : isSlotContact ? 'slot' : 'seminar';
  const fixedAudienceMode = isSingleContact ? 'single' : isSlotContact ? 'slot' : 'all';
  const [submitMessage, setSubmitMessage] = useState('');
  const [eventRecord, setEventRecord] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...content?.form.defaults,
      audienceMode: fixedAudienceMode,
    },
  });

  useEffect(() => {
    let isMounted = true;

    setIsLoadingPreview(true);
    setPreviewError('');

    Promise.all([
      fetchYowanAdminSeminarParticipantMailPreview({
        seminarId: eventId,
        scope: audienceScope,
        applicationId: isSingleContact ? applicationId : undefined,
        slotId: isSlotContact ? slotId : undefined,
      }),
      fetchYowanAdminSeminarDetail(eventId).catch(() => null),
    ])
      .then(([previewPayload, seminarDetail]) => {
        if (!isMounted) {
          return;
        }

        setPreview(previewPayload);
        setEventRecord(seminarDetail);
        reset({
          ...content?.form.defaults,
          audienceMode: fixedAudienceMode,
          subject: previewPayload.default_subject || previewPayload.defaultSubject || '',
          body: previewPayload.default_body || previewPayload.defaultBody || '',
        });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setPreview(null);
        setEventRecord(null);
        setPreviewError(getParticipantMailErrorMessage(
          error,
          '参加者メールの宛先を確認できませんでした。',
        ));
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingPreview(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applicationId, audienceScope, content?.form.defaults, eventId, fixedAudienceMode, isSingleContact, isSlotContact, reset, slotId]);

  if (!content) {
    return null;
  }

  const recipients = preview?.recipients || [];
  const participantRecipients = preview?.participantRecipients || preview?.participant_recipients || [];
  const internalRecipients = preview?.internalRecipients || preview?.internal_recipients || [];
  const recipientsCount = preview?.recipientsCount ?? preview?.recipients_count ?? recipients.length;
  const participantRecipientsCount = preview?.participantRecipientsCount ??
    preview?.participant_recipients_count ??
    participantRecipients.length;
  const internalRecipientsCount = preview?.internalRecipientsCount ??
    preview?.internal_recipients_count ??
    internalRecipients.length;
  const recipientBreakdownSummary = preview?.recipientBreakdownSummary ||
    `参加者 ${participantRecipientsCount}名 / 内部共有 ${internalRecipientsCount}名 / 合計 ${recipientsCount}名`;
  const headerTitle = eventRecord ? `${eventRecord.title} 参加者メール送信` : content.header.title;
  const selectedSlot = isSlotContact && Array.isArray(eventRecord?.slots)
    ? eventRecord.slots.find((slot) => String(slot.id) === String(slotId)) || null
    : null;
  const selectedSlotLabel = selectedSlot
    ? [selectedSlot.title, selectedSlot.datetimeLabel].filter(Boolean).join(' / ') || selectedSlot.datetimeLabel || selectedSlot.id
    : '';
  const relatedLinks = [
    ...(content.relatedLinks || []),
    eventRecord ? { label: '参加者一覧', href: `/admin/events/${eventId}/applications` } : null,
    eventRecord ? { label: 'セミナー編集', href: `/admin/events/${eventId}/edit` } : null,
  ].filter(Boolean);
  const audienceModeOptions = fixedAudienceMode === 'slot'
    ? [
      ...(content.form.options.audienceMode || []),
      { value: 'slot', label: '選択したコマ' },
    ].filter((option, index, options) => options.findIndex((current) => current.value === option.value) === index)
    : content.form.options.audienceMode;
  const recipientDescription = isSingleContact
    ? '宛先は対象の1申込者と管理者共有です。メールはBCCで送信されます。'
    : isSlotContact
      ? `宛先は${selectedSlotLabel ? `「${selectedSlotLabel}」` : '選択したコマ'}に参加する申込済み参加者と、主催者・担当講師・管理者共有です。メールはBCCで送信されます。`
      : '宛先はこのセミナーに申込済みの参加者と、主催者・講師・管理者共有です。メールはBCCで送信されます。';

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage('送信中です。');

    try {
      const payload = await sendYowanAdminSeminarParticipantMail(eventId, {
        ...values,
        scope: audienceScope,
        applicationId: isSingleContact ? applicationId : undefined,
        slotId: isSlotContact ? slotId : undefined,
      });

      setSubmitMessage(`送信処理を受け付けました。対象: ${payload.recipientBreakdownSummary || `合計 ${payload.recipientsCount || payload.recipients_count || 0}名`}`);
      navigate(appendYowanPreviewQueryIfNeeded(`/admin/events/${eventId}/applications`, preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getParticipantMailErrorMessage(
        error,
        '参加者メールを送信できませんでした。入力内容と宛先を確認してください。',
      ));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={headerTitle}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="back-to-applications"
            to={appendYowanPreviewQueryIfNeeded(`/admin/events/${eventId}/applications`, preservedDomainSearch)}
          >
            一覧へ戻る
          </Link>,
        ]}
      />

      <AdminRelatedLinks links={relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoadingPreview ? <LoadingSpinner label="宛先を確認中" showLabel /> : null}
        {!isLoadingPreview && previewError ? (
          <p className="yowan-admin-form__message">{previewError}</p>
        ) : null}

        <div style={previewStyle}>
          <p className="yowan-admin-page-header__lead" style={{ margin: 0 }}>
            宛先プレビュー: {recipientBreakdownSummary}
          </p>
          <p className="yowan-admin-page-header__lead" style={{ margin: 0 }}>
            {recipientDescription}
          </p>
          <div style={previewGroupStyle}>
            <p style={previewGroupTitleStyle}>参加者 {participantRecipientsCount}名</p>
            <ul style={previewListStyle}>
              {participantRecipients.map((recipient) => (
                <li key={recipient} style={previewItemStyle}>
                  {recipient}
                </li>
              ))}
              {!participantRecipients.length ? (
                <li style={previewItemStyle}>対象参加者はまだありません</li>
              ) : null}
            </ul>
          </div>
          <div style={previewGroupStyle}>
            <p style={previewGroupTitleStyle}>内部共有 {internalRecipientsCount}名</p>
            <ul style={previewListStyle}>
              {internalRecipients.map((recipient) => (
                <li key={recipient} style={previewItemStyle}>
                  {recipient}
                </li>
              ))}
              {!internalRecipients.length ? (
                <li style={previewItemStyle}>内部共有宛先はありません</li>
              ) : null}
            </ul>
          </div>
        </div>

        <label className="c-form__field">
          <span className="c-form__label">{content.form.labels.audienceMode}</span>
          <select className="c-form__control" disabled {...register('audienceMode')}>
            {audienceModeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {content.form.audienceModeHelpText ? (
            <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
              {isSingleContact
                ? '対象申込者と管理者共有を宛先にします。'
                : isSlotContact
                  ? '選択したコマの参加者と内部共有を宛先にします。'
                  : 'このセミナーの参加者と内部共有を宛先にします。'}
            </p>
          ) : null}
        </label>

        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.subject}
            <em className="c-form__required">*</em>
          </span>
          <input
            className="c-form__control"
            type="text"
            {...register('subject', { required: content.form.errors.required })}
          />
          {errors?.subject ? (
            <em className="yowan-admin-form__error">{errors.subject.message}</em>
          ) : null}
        </label>

        <label className="c-form__field">
          <span className="c-form__label">
            {content.form.labels.body}
            <em className="c-form__required">*</em>
          </span>
          <textarea
            className="c-form__control c-form__control--textarea"
            rows="9"
            {...register('body', { required: content.form.errors.required })}
          />
          {errors?.body ? (
            <em className="yowan-admin-form__error">{errors.body.message}</em>
          ) : null}
        </label>

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoadingPreview || Boolean(previewError)}>
            {isSubmitting ? (
              <>
                <LoadingSpinner inline size="sm" />
                <span>送信中です。</span>
              </>
            ) : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminEventApplicationMailPage;
